import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { environment } from '@env/environment';
import * as d3 from 'd3';
import { isNil } from 'lodash';

@Component({
  selector: 'app-line-gauge-chart-rainbow',
  templateUrl: './line-gauge-chart-rainbow.component.html',
  styleUrls: ['./line-gauge-chart-rainbow.component.scss'],
  providers: [LocalNumberPipe],
})
export class LineGaugeChartRainbowComponent implements OnChanges {
  @Input() dataRange = { minValue: 0, maxValue: 150 };
  @Input() data: any[] = [];

  consumption: any;
  upperData: any[] = [];
  lowerData: any[] = [];

  svgWidth = 590;
  svgHeight = 210;
  xCoordinateLinearScale = 325;
  xCoordinateRectangle = 81;
  scaleDifference = 35;
  rainbowScaleWidth = 490;
  middleScaleWidth = this.rainbowScaleWidth - this.scaleDifference;
  svg: any;
  lineGaugeSVG: any;
  xScale: any;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && this.data.length > 0) {
      this.upperData = [];
      this.lowerData = [];

      this.renderRainbowBar();
      this.renderScale(true);
      this.renderMarkers(this.xScale);
      this.renderLabels(this.xScale);
    }
  }

  renderRainbowBar() {
    if (this.svg) {
      this.svg.remove();
    }

    this.svg = d3.select('.line-guage-chart-section').append('svg').attr('class', 'graph').attr('width', this.svgWidth).attr('height', this.svgHeight);

    this.lineGaugeSVG = this.svg.append('g').attr('class', 'g-wrapper').attr('transform', 'translate(-25,-25)');

    this.lineGaugeSVG
      .append('rect')
      .attr('class', 'rectangle-gauge')
      .attr('x', this.xCoordinateRectangle)
      .attr('y', 100)
      .attr('ry', 3)
      .attr('width', this.rainbowScaleWidth)
      .attr('height', 12.5)
      .style('fill', 'url("#linear")');

    this.lineGaugeSVG
      .append('rect')
      .attr('class', 'rectangle-gauge')
      .attr('x', this.xCoordinateRectangle)
      .attr('y', 131.5)
      .attr('ry', 3)
      .attr('width', this.rainbowScaleWidth)
      .attr('height', 12.5)
      .style('fill', 'url("#linear")');
  }

  renderScale(isInverted: boolean) {
    if (isInverted) {
      this.xScale = d3
        .scaleLinear()
        .domain([this.dataRange.minValue, this.dataRange.maxValue])
        .range([this.middleScaleWidth / 2, -this.middleScaleWidth / 2]);
    } else {
      this.xScale = d3
        .scaleLinear()
        .domain([this.dataRange.minValue, this.dataRange.maxValue])
        .range([-this.middleScaleWidth / 2, this.middleScaleWidth / 2]);
    }

    const step = this.dataRange.maxValue / 10;
    const tickValues = [];
    for (let i = 0; i <= this.dataRange.maxValue; i++) {
      if (i % step === 0) {
        tickValues.push(i);
      }
    }

    const xAxis = d3.axisBottom(this.xScale).tickValues(tickValues).tickSize(0);

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'axis')
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 115.5)')
      .call(xAxis);
  }

  renderMarkers(xScale: any) {
    const dataWithMaxRange = this.data;
    dataWithMaxRange.forEach((el) => {
      el.maxValue = this.dataRange.maxValue;
    });

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'data-markers')
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(dataWithMaxRange)
      .enter()
      .append('image')
      .attr('class', 'arrow')
      .attr('transform', function (d: any) {
        if (!isNil(d.value)) {
          if (d.value > d.maxValue) {
            return 'translate(' + xScale(d.maxValue) + ', -80)';
          } else {
            return 'translate(' + xScale(d.value) + ', -80)';
          }
        }
      })
      .attr('xlink:href', function (d: any) {
        if (d.indicator === 'companyColor' && !isNil(d.value)) {
          return `./assets/icons/arrow-${environment.tenant.trim().toLowerCase()}.svg`;
        }
        if (d.indicator === 'green' && !isNil(d.value)) {
          return `./assets/icons/arrow-green.svg`;
        }
      })
      .attr('width', 28)
      .attr('height', 38)
      .attr('x', function (d: any) {
        if (!isNil(d.value)) {
          if (d.value > d.maxValue) {
            if (d.ranking) {
              return -30;
            } else {
              return 4;
            }
          } else {
            return -14;
          }
        }
      })
      .attr('y', 161);

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'data-markers')
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(this.data)
      .enter()
      .append('image')
      .attr('transform', function (d: any) {
        if (!isNil(d.value)) {
          return 'translate(' + xScale(d.value) + ', 132)';
        }
      })
      .attr('xlink:href', function (d: any) {
        if (d.indicator === 'lightGrey' && !isNil(d.value)) {
          return './assets/icons/arrow-light-grey.svg';
        }
        if (d.indicator === 'darkGrey' && !isNil(d.value)) {
          return './assets/icons/arrow-dark-grey.svg';
        }
      })
      .attr('width', 28)
      .attr('height', 38)
      .attr('x', -14)
      .attr('y', -5);
  }

  renderLabels(xScale: any) {
    this.upperData.push(this.data.find((value) => value['indicator'] === 'companyColor'));
    this.lowerData.push(this.data.find((value) => value['indicator'] === 'lightGrey'));
    const is4thArrow = this.data.find((value) => value['indicator'] === 'green');

    if (is4thArrow !== undefined) {
      this.upperData.push(this.data.find((value) => value['indicator'] === 'green'));
      const dataWithMaxRange = this.upperData;
      dataWithMaxRange.forEach((el) => {
        el.maxValue = this.dataRange.maxValue;
      });
      let isOverlapping = false;
      let reference;
      const xValuesDifference = dataWithMaxRange[0].value - dataWithMaxRange[1].value;
      if (dataWithMaxRange[0].value < dataWithMaxRange[1].value) {
        reference = dataWithMaxRange[0].value;
      } else {
        reference = dataWithMaxRange[1].value;
      }

      let medianValue;
      if (dataWithMaxRange[0].value > dataWithMaxRange[0].maxValue || dataWithMaxRange[1].value > dataWithMaxRange[1].maxValue) {
        medianValue = dataWithMaxRange[0].maxValue;
      } else {
        medianValue = Math.abs(reference + Math.abs(xValuesDifference) / 2);
      }

      const thresholdDistance = 24;
      isOverlapping =
        xValuesDifference < thresholdDistance ||
        (dataWithMaxRange[0].value > dataWithMaxRange[0].maxValue && dataWithMaxRange[1].value > dataWithMaxRange[1].maxValue);

      if (isOverlapping) {
        this.renderOverlappingLabels(xScale, medianValue, xValuesDifference, reference);
      } else {
        this.render2LayeredLabelsTop(this.xScale);
      }
    } else {
      this.render2LayeredLabelsTop(this.xScale);
    }

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'data-labels')
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(this.lowerData)
      .enter()
      .append('rect')
      .attr('class', 'label-container')
      .attr('transform', function (d: any) {
        if (!isNil(d.value)) {
          return 'translate(' + xScale(d.value) + ', -80)';
        }
      })
      .attr('x', -36)
      .attr('y', 235)
      .attr('ry', 2)
      .attr('width', 72)
      .attr('height', 27);

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'data-labels')
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(this.lowerData)
      .enter()
      .append('text')
      .attr('class', 'label-text')
      .text(function (d: any) {
        if (!isNil(d.value)) {
          return 'von ' + d.municipalities.toString();
        }
      })
      .attr('transform', function (d: any) {
        if (!isNil(d.value)) {
          return 'translate(' + xScale(d.value) + ', 50)';
        }
      })
      .attr('x', 0)
      .attr('y', 128);

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'data-labels')
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(this.lowerData)
      .enter()
      .append('text')
      .attr('class', 'label-text')
      .text(function (d: any) {
        if (!isNil(d.value)) {
          return 'Platz ' + d.ranking.toString();
        }
      })
      .attr('transform', function (d: any) {
        if (!isNil(d.value)) {
          return 'translate(' + xScale(d.value) + ', 50)';
        }
      })
      .attr('x', 0)
      .attr('y', 116);
  }

  renderOverlappingLabels(xScale: any, medianValue: number, xValuesDifference: number, reference: string) {
    this.lineGaugeSVG
      .append('g')
      .attr('class', 'data-labels')
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(this.upperData)
      .enter()
      .append('rect')
      .attr('class', 'label-container')
      .attr('transform', 'translate(' + xScale(medianValue) + ', -80)')
      .attr('x', -70)
      .attr('y', 142)
      .attr('ry', 2)
      .attr('width', 140)
      .attr('height', 27);

    for (const element of this.upperData) {
      const data = [];
      let x;
      if (xValuesDifference === 0) {
        if (element.ranking > this.upperData[1].ranking) {
          data.push(element);
          x = 0;
        } else {
          data.push(element);
          x = -68;
        }
      } else {
        if (element.value === reference) {
          data.push(element);
          x = 0;
        } else {
          data.push(element);
          x = -68;
        }
      }

      this.lineGaugeSVG
        .append('g')
        .attr('class', 'data-markers-small')
        .attr('y', 220)
        .attr('width', this.middleScaleWidth)
        .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
        .selectAll('dot')
        .data(data)
        .enter()
        .append('image')
        .attr('class', 'arrow')
        .attr('transform', 'translate(' + xScale(medianValue) + ', -80)')
        .attr('xlink:href', function (d: any) {
          if (d.indicator === 'companyColor' && !isNil(d.value)) {
            return `./assets/icons/arrow-${environment.tenant.trim().toLowerCase()}.svg`;
          }
          if (d.indicator === 'green' && !isNil(d.value)) {
            return `./assets/icons/arrow-green.svg`;
          }
        })
        .attr('width', 18)
        .attr('height', 28)
        .attr('x', x)
        .attr('y', 142);
    }

    for (let i = 0; i < this.upperData.length; i++) {
      const data = [];
      let x;
      if (xValuesDifference === 0) {
        if (this.upperData[i].ranking > this.upperData[1].ranking) {
          data.push(this.upperData[i]);
          x = 40;
        } else {
          data.push(this.upperData[i]);
          x = -28;
        }
      } else {
        if (this.upperData[i].value === reference) {
          data.push(this.upperData[i]);
          x = 40;
        } else {
          data.push(this.upperData[i]);
          x = -28;
        }
      }

      this.lineGaugeSVG
        .append('g')
        .attr('class', 'data-labels')
        .attr('y', 220)
        .attr('width', this.middleScaleWidth)
        .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
        .selectAll('dot')
        .data(data)
        .enter()
        .append('text')
        .attr('class', 'label-text')
        .text(function (d: any) {
          if (!isNil(d.value)) {
            return 'von ' + d.municipalities.toString();
          }
        })
        .attr('transform', 'translate(' + xScale(medianValue) + ', 50)')
        .attr('x', x)
        .attr('y', 34);

      this.lineGaugeSVG
        .append('g')
        .attr('class', 'data-labels')
        .attr('y', 220)
        .attr('width', this.middleScaleWidth)
        .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
        .selectAll('dot')
        .data(data)
        .enter()
        .append('text')
        .attr('class', 'label-text')
        .text(function (d: any) {
          if (!isNil(d.value)) {
            return 'Platz ' + d.ranking.toString();
          }
        })
        .attr('transform', 'translate(' + xScale(medianValue) + ', 50)')
        .attr('width', 72)
        .attr('height', 100)
        .attr('x', x)
        .attr('y', 23);
    }
  }

  render2LayeredLabelsTop(xScale: any) {
    const dataWithMaxRange = this.upperData;
    dataWithMaxRange.forEach((el) => {
      el.maxValue = this.dataRange.maxValue;
    });

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'data-labels')
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(dataWithMaxRange)
      .enter()
      .append('rect')
      .attr('class', 'label-container')
      .attr('transform', function (a: any) {
        if (!isNil(a.value)) {
          if (a.value > a.maxValue) {
            return 'translate(' + xScale(a.maxValue) + ', -80)';
          } else {
            return 'translate(' + xScale(a.value) + ', -80)';
          }
        }
      })
      .attr('x', -54)
      .attr('x', function (e: any) {
        if (!isNil(e.value)) {
          if (e.value > e.maxValue) {
            return -54;
          } else {
            return -36;
          }
        }
      })
      .attr('y', 142)
      .attr('ry', 2)
      .attr('width', 72)
      .attr('height', 27);

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'data-labels')
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(dataWithMaxRange)
      .enter()
      .append('text')
      .attr('class', 'label-text')
      .text(function (d: any) {
        if (!isNil(d.value)) {
          return 'von ' + d.municipalities.toString();
        }
      })
      .attr('transform', function (e: any) {
        if (!isNil(e.value)) {
          if (e.value > e.maxValue) {
            return 'translate(' + xScale(e.maxValue) + ', 50)';
          } else {
            return 'translate(' + xScale(e.value) + ', 50)';
          }
        }
      })
      .attr('x', function (d: any) {
        if (!isNil(d.value)) {
          if (d.value > d.maxValue) {
            return -18;
          } else {
            return 0;
          }
        }
      })
      .attr('y', 34);

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'data-labels')
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(dataWithMaxRange)
      .enter()
      .append('text')
      .attr('class', 'label-text')
      .text(function (d: any) {
        if (!isNil(d.value)) {
          return 'Platz ' + d.ranking.toString();
        }
      })
      .attr('transform', function (y: any) {
        if (!isNil(y.value)) {
          if (y.value > y.maxValue) {
            return 'translate(' + xScale(y.maxValue) + ', 50)';
          } else {
            return 'translate(' + xScale(y.value) + ', 50)';
          }
        }
      })
      .attr('width', 72)
      .attr('height', 100)
      .attr('x', function (f: any) {
        if (!isNil(f.value)) {
          if (f.value > f.maxValue) {
            return -18;
          } else {
            return 0;
          }
        }
      })
      .attr('y', 23);
  }
}
